import {
  SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { SELECTION_COLOR_VARIANT_INFO } from "@is/constants";
import { ViewStateContext } from "@is/contexts";
import { useStyleDetails } from "@is/hooks";
import { useManageBasket } from "@is/hooks/basket";
import { TrackingEventTypes, trackEvent } from "@is/services/EventTracking";
import { BasketItemPrice, MediaOrderingRule, ProductItem } from "@is/types";
import {
  getAllSizesAndLengths,
  getMainImageUrl,
  getSizesAndLengthsOfSelectedColorVariant,
} from "@is/utils";

const OUT_OF_STOCK_NOTIFICATION_TIMEOUT = 2000;
const IS_ADDED_TIMEOUT = 2000;

type Props = {
  item: ProductItem;
  skipRequest?: boolean;
  onReset?: () => void;
  onBasketAdded?: () => void;
};

export const useWidgetProductDetails = ({
  item,
  skipRequest = false,
  onReset,
  onBasketAdded,
}: Props) => {
  const {
    basketItems,
    updateBasket,
    isPending: isBasketItemsPending,
  } = useManageBasket();

  const [selectedColorVariantId, setSelectedColorVariantId] = useState<
    string | null
  >(item.id);
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [selectedLength, setSelectedLength] = useState<string | null>(null);
  const [isAdded, setIsAdded] = useState<boolean>(false);
  const [showOutOfStockNotification, setShowOutOfStockNotification] =
    useState<boolean>(false);
  const [colorName, setColorName] = useState<string | null>(null);

  const { data: styleDetails } = useStyleDetails(
    item.styleId,
    item.searchSizesAndLengths,
    skipRequest
  );

  const { updateViewState } = useContext(ViewStateContext).content;

  const handleQuickAddToBasketWrapperClick = useCallback(
    (e: SyntheticEvent) => {
      e.stopPropagation();
    },
    []
  );

  const selectedColorVariant = useMemo(
    () =>
      styleDetails?.variants.find(({ id }) => id === selectedColorVariantId) ??
      styleDetails?.variants[0],
    [selectedColorVariantId, styleDetails]
  );

  const isLengthRequired = useMemo(
    () =>
      selectedColorVariant?.sizeInfos?.some((sizeInfo) => !!sizeInfo.length),
    [selectedColorVariant]
  );

  const findBasketItemByEan = useCallback(
    (ean: string) => {
      const basketItem = basketItems?.find((item) => item.ean === ean);
      return basketItem;
    },
    [basketItems]
  );

  const selectedSizeInfos = useMemo(() => {
    if (!selectedColorVariant?.sizeInfos?.length) {
      return null;
    }

    if (!selectedSize || (isLengthRequired && !selectedLength)) {
      return null;
    }

    let sizeInfos;

    if (isLengthRequired) {
      sizeInfos = selectedColorVariant?.sizeInfos?.find(
        (sizeInfo) =>
          sizeInfo.size === selectedSize && sizeInfo.length === selectedLength
      );
    } else {
      sizeInfos = selectedColorVariant?.sizeInfos?.find(
        (sizeInfo) => sizeInfo.size === selectedSize
      );
    }

    if (sizeInfos) {
      const basketItem = findBasketItemByEan(sizeInfos.ean);

      return {
        ...sizeInfos,
        availableToOrder:
          (sizeInfos.onlineQuantity ?? 0) - (basketItem?.quantity || 0),
      };
    }

    return {
      ean: "",
      instoreQuantity: 0,
      onlineQuantity: 0,
      availableToOrder: 0,
    };
  }, [
    findBasketItemByEan,
    selectedColorVariant,
    selectedSize,
    selectedLength,
    isLengthRequired,
  ]);

  const variants = useMemo(() => styleDetails?.variants || [], [styleDetails]);

  const mediaUrl = useMemo(() => {
    const medias = selectedColorVariant?.media
      ? selectedColorVariant.media
      : item.media;

    const orderRule = MediaOrderingRule.ColorThumbnails;

    return getMainImageUrl(medias, orderRule);
  }, [item, selectedColorVariant]);

  const getSizesAndLengths = useCallback(
    (size?: string) =>
      getSizesAndLengthsOfSelectedColorVariant(selectedColorVariant, size),
    [selectedColorVariant]
  );

  const { sizes, lengths } = useMemo(
    () => getAllSizesAndLengths(variants),
    [variants]
  );

  const variantsWithSelectedSize = useMemo(() => {
    if (!selectedSize) {
      return [];
    }

    if (isLengthRequired && !selectedLength) {
      return [];
    }

    return variants.reduce((acc: string[], variant) => {
      if (
        variant.sizeInfos.some((sizeInfo) => {
          const basketItem = findBasketItemByEan(sizeInfo.ean);

          const availableToOrder =
            (sizeInfo.onlineQuantity ?? 0) - (basketItem?.quantity || 0);

          return (
            sizeInfo.size === selectedSize &&
            (!isLengthRequired || sizeInfo.length === selectedLength) &&
            availableToOrder > 0
          );
        })
      ) {
        acc.push(variant.id);
      }
      return acc;
    }, []);
  }, [
    findBasketItemByEan,
    isLengthRequired,
    selectedLength,
    selectedSize,
    variants,
  ]);

  const { sizes: sizesForColorVariant, lengths: lengthsForColorVariant } =
    useMemo(() => {
      if (!selectedColorVariant) {
        return { sizes: [], lengths: [] };
      }

      return selectedColorVariant?.sizeInfos?.reduce(
        (acc, sizeInfo) => {
          const basketItem = findBasketItemByEan(sizeInfo.ean);

          const availableToOrder =
            (sizeInfo.onlineQuantity ?? 0) - (basketItem?.quantity || 0);

          if (
            sizeInfo.size &&
            !acc.sizes.includes(sizeInfo.size) &&
            availableToOrder > 0
          ) {
            acc.sizes.push(sizeInfo.size);
          }

          if (
            sizeInfo.length &&
            !acc.lengths.includes(sizeInfo.length) &&
            availableToOrder > 0
          ) {
            acc.lengths.push(sizeInfo.length);
          }

          return acc;
        },
        {
          sizes: [],
          lengths: [],
        }
      );
    }, [selectedColorVariant, findBasketItemByEan]);

  const handleTrackColorVariantInfoSelection = useCallback(
    ({
      size,
      length,
      colorVariantId,
      selectionType,
    }: {
      size?: string;
      length?: string;
      colorVariantId?: string;
      selectionType?: string;
    }) => {
      const newSelectedSize = size || selectedSize;
      const newSelectedLength = length || selectedLength;
      const newSelectedColorVariant = colorVariantId
        ? styleDetails?.variants.find(({ id }) => id === colorVariantId)
        : selectedColorVariant;

      let selectedSizeInfo;

      if (isLengthRequired) {
        selectedSizeInfo = newSelectedColorVariant?.sizeInfos?.find(
          (sizeInfo) =>
            sizeInfo.size === newSelectedSize &&
            sizeInfo.length === newSelectedLength
        );
      } else {
        selectedSizeInfo = newSelectedColorVariant?.sizeInfos?.find(
          (sizeInfo) => sizeInfo.size === newSelectedSize
        );
      }

      if (selectedSizeInfo) {
        const basketItem = findBasketItemByEan(selectedSizeInfo.ean);

        selectedSizeInfo = {
          ...selectedSizeInfo,
          availableToOrder:
            selectedSizeInfo.onlineQuantity - (basketItem?.quantity || 0),
        };
      }

      if (selectedSizeInfo?.availableToOrder === 0) {
        const eventData = {
          ean: selectedSizeInfo.ean,
          styleId: item.styleId,
          variantId: colorVariantId || selectedColorVariantId,
        };

        trackEvent({
          eventType: TrackingEventTypes.SelectOutOfStock,
          data: eventData,
        });
      }

      const ean = selectedSizeInfo?.ean || "";

      trackEvent({
        eventType: SELECTION_COLOR_VARIANT_INFO[selectionType],
        data: {
          quickAddToBasket: true,
          styleId: item.styleId,
          variantId: colorVariantId || selectedColorVariantId,
          ...(!!ean && { ean }),
        },
      });
    },
    [
      findBasketItemByEan,
      isLengthRequired,
      item,
      selectedColorVariant,
      selectedColorVariantId,
      selectedLength,
      selectedSize,
      styleDetails,
    ]
  );

  const handleSelectVariant = useCallback(
    (e: SyntheticEvent) => {
      e.stopPropagation();
      const variantId = e.currentTarget.getAttribute("data-id");

      setSelectedColorVariantId(variantId);

      const colorVariant =
        styleDetails?.variants.find(({ id }) => id === variantId) ??
        styleDetails?.variants[0];

      if (colorVariant.sizeInfos.length === 1) {
        setSelectedSize(colorVariant.sizeInfos[0].size);
      }

      handleTrackColorVariantInfoSelection({
        colorVariantId: variantId,
        selectionType: "colorVariantId",
      });
    },
    [handleTrackColorVariantInfoSelection, styleDetails?.variants]
  );

  const handleSelectSize = useCallback(
    (e: SyntheticEvent) => {
      e.stopPropagation();

      let size = e.currentTarget.getAttribute("data-id");
      if (selectedSize === size) {
        size = null;
      }
      setSelectedSize(size);
      const { lengths } = getSizesAndLengths(size);
      if (lengths.length === 1) {
        setSelectedLength(lengths[0]);
      } else {
        setSelectedLength(null);
      }

      handleTrackColorVariantInfoSelection({
        size,
        selectionType: "size",
      });
    },
    [handleTrackColorVariantInfoSelection, getSizesAndLengths, selectedSize]
  );

  const handleSelectLength = useCallback(
    (e: SyntheticEvent) => {
      e.stopPropagation();

      let length = e.currentTarget.getAttribute("data-id");
      if (selectedLength === length) {
        length = null;
      }
      setSelectedLength(length);

      handleTrackColorVariantInfoSelection({
        length,
        selectionType: "length",
      });
    },
    [handleTrackColorVariantInfoSelection, selectedLength]
  );

  const handleReset = useCallback(() => {
    if (onReset) {
      onReset();
      return;
    }

    setSelectedColorVariantId(item.id);
    setSelectedSize(null);
    setSelectedLength(null);
    setIsAdded(false);
    setShowOutOfStockNotification(false);
    updateViewState({ quickAddToBasketItem: null });
  }, [item, updateViewState, onReset]);

  const handleBasketButtonClick = useCallback(async () => {
    if (showOutOfStockNotification) {
      return;
    }

    if (!selectedSizeInfos) {
      return;
    }

    if (selectedSizeInfos.availableToOrder === 0) {
      setShowOutOfStockNotification(true);

      setTimeout(() => {
        setShowOutOfStockNotification(false);
        setSelectedLength(null);
      }, OUT_OF_STOCK_NOTIFICATION_TIMEOUT);

      trackEvent({
        eventType: TrackingEventTypes.FailedAddingToBasketDueToStockLimit,
        data: {
          ean: selectedSizeInfos.ean,
          quantity: 1,
          styleId: item.styleId,
          variantId: selectedColorVariantId,
        },
      });

      return;
    }

    const hasBasketItem = basketItems?.some(
      (basketItem) => basketItem.ean === selectedSizeInfos.ean
    );

    const preparedBasketItems =
      basketItems?.map((item) => ({
        ean: item.ean,
        productId: item.productId,
        price: item.price,
        quantity: item.quantity,
      })) || [];

    const newBasketItems = hasBasketItem
      ? preparedBasketItems.map((item) => {
          if (item.ean === selectedSizeInfos.ean) {
            return {
              ...item,
              quantity: item.quantity + 1,
            };
          }

          return item;
        })
      : [
          ...preparedBasketItems,
          {
            ean: selectedSizeInfos.ean,
            productId: item.styleId,
            quantity: 1,
            price: item.price as BasketItemPrice,
          },
        ];

    const updateBasketResult = await updateBasket({
      items: newBasketItems,
    });

    if (!updateBasketResult) {
      return;
    }

    trackEvent({
      eventType: TrackingEventTypes.BasketQuantityChanged,
      data: {
        ean: selectedSizeInfos.ean,
        quantity: 1,
        styleId: item.styleId,
        variantId: selectedColorVariantId,
      },
    });

    setIsAdded(true);

    if (onBasketAdded) {
      onBasketAdded();
    }

    setTimeout(() => {
      handleReset();
    }, IS_ADDED_TIMEOUT);
  }, [
    basketItems,
    handleReset,
    item,
    selectedColorVariantId,
    selectedSizeInfos,
    showOutOfStockNotification,
    updateBasket,
    onBasketAdded,
  ]);

  const showUnavailableSlash = useMemo(() => {
    if (isLengthRequired) {
      return selectedSize && selectedLength;
    }

    return !!selectedSize;
  }, [isLengthRequired, selectedLength, selectedSize]);

  useEffect(() => {
    setShowOutOfStockNotification(false);
  }, [selectedSizeInfos]);

  useEffect(() => {
    if (sizes?.length === 1) {
      setSelectedSize(sizes[0]);
    }
  }, [sizes]);

  useEffect(() => {
    if (isLengthRequired && lengths?.length === 1 && !!selectedSize) {
      setSelectedLength(lengths[0]);
    }
  }, [isLengthRequired, lengths, selectedSize]);

  useEffect(() => {
    if (selectedColorVariant) {
      setColorName(selectedColorVariant.color.name);
    } else {
      setColorName(item.color || "");
    }
  }, [item, selectedColorVariant]);

  return {
    // Selection state
    selectedColorVariant,
    selectedColorVariantId,
    selectedSize,
    selectedLength,
    colorName,

    // Available options
    variants,
    sizes,
    lengths,
    sizesForColorVariant,
    lengthsForColorVariant,
    variantsWithSelectedSize,

    // Product details
    styleDetails,
    mediaUrl,
    isLengthRequired,
    showUnavailableSlash,

    // Basket state
    isAdded,
    isBasketItemsPending,
    basketItems,
    showOutOfStockNotification,

    // State setters
    setSelectedColorVariantId,
    setSelectedSize,
    setSelectedLength,
    setIsAdded,
    setShowOutOfStockNotification,
    setColorName,

    // Event handlers
    handleQuickAddToBasketWrapperClick,
    handleSelectVariant,
    handleSelectSize,
    handleSelectLength,
    handleReset,
    handleBasketButtonClick,
    updateBasket,

    // View state
    updateViewState,
  };
};
